'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useTheme } from '../theme'

// Footer link interface
export interface FooterLink {
  label: string
  href: string
}

// Footer section interface
export interface FooterSection {
  title: string
  links: FooterLink[]
}

export interface FooterProps {
  /** Site logo URL */
  logoUrl: string
  /** Site title */
  siteTitle: string
  /** Site description/tagline */
  siteDescription?: string
  /** Current locale (e.g., 'en', 'fr', 'es') */
  locale?: string
  /** Footer sections (columns) */
  sections: FooterSection[]
  /** Copyright text */
  copyrightText: string
  /** Function to generate localized paths */
  getLocalizedPath?: (path: string, locale: string) => string
  /** Logo size in pixels (default: 32) */
  logoSize?: number
  /** Grid columns for desktop (default: 4, max sections + 2 for logo column) */
  gridCols?: number
  /** Additional className */
  className?: string
}

export function Footer({
  logoUrl,
  siteTitle,
  siteDescription,
  locale = 'en',
  sections,
  copyrightText,
  getLocalizedPath = (path) => path,
  logoSize = 32,
  gridCols = 4,
  className = '',
}: FooterProps) {
  const { theme } = useTheme()

  // Theme colors
  const primaryColor = theme.colors.primary
  const secondaryColor = theme.colors.secondary || theme.colors.primary
  const backgroundColor = theme.components?.footer?.background || theme.colors.background || '#111318'
  const textColor = theme.components?.footer?.textColor || '#d1d5db'
  const headingColor = theme.components?.footer?.headingColor || secondaryColor
  const borderColor = theme.components?.footer?.borderColor || '#374151'

  return (
    <footer className={`py-12 ${className}`} style={{ backgroundColor }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 md:grid-cols-${gridCols} gap-8`}>
          {/* Logo and Description */}
          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <div className="relative mr-3" style={{ width: logoSize, height: logoSize }}>
                <Image
                  src={logoUrl}
                  alt={`${siteTitle} Logo`}
                  fill
                  className="object-contain"
                  sizes={`${logoSize}px`}
                />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#ffffff' }}>
                {siteTitle}
              </h3>
            </div>
            {siteDescription && (
              <p className="mb-6 max-w-md" style={{ color: textColor }}>
                {siteDescription}
              </p>
            )}
          </div>

          {/* Footer Sections */}
          {sections.map((section, index) => (
            <div key={index}>
              <h4 className="text-lg font-semibold mb-4" style={{ color: headingColor }}>
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={getLocalizedPath(link.href, locale)}
                      className="hover:opacity-80 transition-opacity duration-200"
                      style={{ color: textColor }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t mt-8 pt-8 text-center" style={{ borderTopColor: borderColor }}>
          <p style={{ color: '#9ca3af' }}>{copyrightText}</p>
        </div>
      </div>
    </footer>
  )
}
